import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { updateMany } from "../../../../../api/onboarding";
import { GeaoContext } from "../../../GeaoContextProvider";
import { ApiRequestStatus } from "../../../constants/ApiRequestStatus";
import { GeaoPaths } from "../../../constants/GeaoPaths";
import { formDataToQuestionAnswers } from "./mappers";
import { mapToOnboardingPageSubmitRequest } from "../../../mappers/OnboardingPageSubmitRequestMapper";
import IdentityType from "../../../constants/IdentityType";
import { getBankName } from "../../../../../api/billing";

/**
 * Custom React hook for handling form events in the Principal flow Banking Information page.
 * Manages form submission, save and exit functionality, and API request states.
 * 
 * @param {Function} [getValues=() => ({})] - Function to retrieve current form values
 * @param {Function} [trigger=() => Promise.resolve()] - Function to trigger form validation
 * @returns {Object} Object containing form event handlers and loading state
 * @returns {Function} returns.onSubmit - Handler for form submission that navigates to next page
 * @returns {Function} returns.onSaveAndExit - Handler for saving progress and returning to checklist
 * @returns {boolean} returns.saving - Boolean indicating if an API request is in progress
 * @returns {boolean} returns.hasBankingError - Boolean indicating if there are banking assessment notices
 */
export default function useFormEvents(
    getValues = () => ({}),
    trigger = () => { return Promise.resolve(); }
) {
    const navigate = useNavigate();

    const {
        agencyInterview,
        setAgencyInterview,
        agentInterview,
        setAgentInterview
    } = useContext(GeaoContext);

    const [updateStatus, setUpdateStatus] = useState(ApiRequestStatus.NotSent);
    const [nextPath, setNextPath] = useState(GeaoPaths.CreateYourProfileChecklist);
    const [hasBankingError, setHasBankingError] = useState(false);

    const saving = updateStatus === ApiRequestStatus.Pending;

    /**
     * Effect hook that handles navigation after successful API updates.
     * Navigates to the specified next path after successful update if there are no banking errors.
     */
    useEffect(() => {
        if (updateStatus === ApiRequestStatus.Success && !hasBankingError) {
            navigate(nextPath);
        }
    }, [updateStatus, nextPath, navigate, hasBankingError]);

    /**
     * Handles form submission and proceeds to the next step in the principal flow.
     * Sets the next path to Principal W9 Tax Form and updates the interview.
     * 
     * @param {Object} formData - The form data to be submitted
     */
    const onSubmit = (formData) => {
        const routingNumber = formData?.routingNumber;
        getBankName(routingNumber, () => {
            setNextPath(GeaoPaths.PrincipalW9TaxForm);
            updateInterview(formData, false);
        }, () => { });
    };

    /**
     * Handles the save and exit action.
     * Triggers form validation, then saves current form values and navigates back to checklist.
     */
    const onSaveAndExit = () => {
        trigger().then(validationPassed => {
            if (validationPassed) {
                const routingNumber = getValues()?.routingNumber;
                getBankName(routingNumber, () => {
                    setNextPath(GeaoPaths.CreateYourProfileChecklist);
                    updateInterview(getValues(), true);
                }, () => { });
            }
        });
    };

    /**
     * Updates both agent and agency interviews with form data via a single API call.
     * Manages API request state and handles success/error scenarios.
     * 
     * @param {Object} formData - The form data to be saved
     * @param {boolean} isSaveAndExit - Whether this is a save and exit operation
     */
    const updateInterview = (formData, isSaveAndExit) => {
        if (updateStatus === ApiRequestStatus.Pending) return;

        const updateAgentInterviewRequest = mapToOnboardingPageSubmitRequest(
            IdentityType.Agent,
            agentInterview,
            "PrincipalBankingInformation",
            isSaveAndExit
        );

        const updateAgencyInterviewRequest = mapToOnboardingPageSubmitRequest(
            IdentityType.Agency,
            agencyInterview,
            "PrincipalBankingInformation",
            isSaveAndExit,
            formData,
            formDataToQuestionAnswers
        );

        setUpdateStatus(ApiRequestStatus.Pending);

        updateMany(
            [updateAgencyInterviewRequest, updateAgentInterviewRequest],
            (responseInterviews) => {
                // Success callback
                const updatedAgencyInterview = responseInterviews.find(i => i.interviewId === agencyInterview.interviewId);
                const updatedAgentInterview = responseInterviews.find(i => i.interviewId === agentInterview.interviewId);

                setAgencyInterview(updatedAgencyInterview);
                setAgentInterview(updatedAgentInterview);

                // Check for banking assessment notices
                const bankingNotices = updatedAgencyInterview?.assessments?.HasBanking?.notices;
                const hasNotices = !!bankingNotices && bankingNotices.length > 0;
                setHasBankingError(hasNotices);

                setUpdateStatus(ApiRequestStatus.Success);
            },
            () => {
                // Finally callback
            },
            () => {
                // Error callback
                setUpdateStatus(ApiRequestStatus.Error);
            }
        );
    };

    return {
        onSubmit,
        onSaveAndExit,
        saving,
        hasBankingError
    };
}